

import * as THREE from "../libs/three.js/build/three.module.js";
import {PointCloudTreeNode} from "./PointCloudTree.js";
import {XHRFactory} from "./XHRFactory.js";
import {Utils} from "./utils.js";
/**
 * Represents the geometry of a point cloud octree. This class holds the information necessary
 * to construct and manage the octree structure of a point cloud.
 * @class
 */
export class PointCloudOctreeGeometry{
	/**
     * Creates an instance of PointCloudOctreeGeometry.
     */
	constructor(){
		/**
         * The URL of the point cloud.
         * @type {string|null}
         */
		this.url = null;
		/**
         * Directory where the octree files are stored.
         * @type {string|null}
         */
		this.octreeDir = null;
		/**
         * The spacing between points at root level.
         * @type {number}
         */
		this.spacing = 0;
		/**
         * The axis-aligned bounding box of the point cloud.
         * @type {THREE.Box3|null}
         */
		this.boundingBox = null;
		/**
         * The root node of the octree.
         * @type {PointCloudOctreeNode|null}
         */
		this.root = null;
		/**
         * The collection of nodes in the octree.
         * @type {Object|null}
         */
		this.nodes = null;
		/**
         * The point attributes of the point cloud.
         * @type {PointAttributes|null}
         */
		this.pointAttributes = null;
		/**
         * The size of hierarchy steps.
         * @type {number}
         */
		this.hierarchyStepSize = -1;
		/**
         * The loader responsible for loading this geometry.
         * @type {any|null}
         */
		this.loader = null;
		/**
         * The tight bounding box of the point cloud.
         * @type {THREE.Box3|null}
         */
		this.tightBoundingBox = null;
	}
	
}

/**
 * Represents a node in the point cloud octree geometry structure.
 * This class extends PointCloudTreeNode and provides specific properties and methods for octree nodes.
 * 
 * @extends PointCloudTreeNode
 */
export class PointCloudOctreeGeometryNode extends PointCloudTreeNode{
	/**
     * Creates an instance of PointCloudOctreeGeometryNode.
     * 
     * @param {string} name - The name of the node.
     * @param {PointCloudOctreeGeometry} pcoGeometry - The associated point cloud octree geometry.
     * @param {THREE.Box3} boundingBox - The bounding box of the node.
     */
	constructor(name, pcoGeometry, boundingBox){
		super();
		/**
         * Unique identifier for the node.
         * @type {number}
         */
		this.id = PointCloudOctreeGeometryNode.IDCount++;
		/**
         * Name of the node.
         * @type {string}
         */
		this.name = name;
		/**
         * Index of the node.
         * @type {number}
         */
		this.index = parseInt(name.charAt(name.length - 1));
		/**
         * The associated point cloud octree geometry.
         * @type {PointCloudOctreeGeometry}
         */
		this.pcoGeometry = pcoGeometry;
		/**
         * The geometry of the node.
         * @type {THREE.BufferGeometry|null}
         */
		this.geometry = null;
		/**
         * The bounding box of the node.
         * @type {THREE.Box3}
         */
		this.boundingBox = boundingBox;
		/**
         * The bounding box of the node.
         * @type {THREE.Box3}
         */
		this.tightBoundingBox = pcoGeometry.tightBoundingBox;
		/**
         * The bounding sphere of the node.
         * @type {THREE.Sphere}
         */
		this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
		/**
         * Child nodes of this node.
         * @type {Object}
         */
		this.children = {};
		/**
         * The number of points in the node.
         * @type {number}
         */
		this.numPoints = 0;
		/**
         * The level of the node in the octree.
         * @type {number|null}
         */
		this.level = null;
		/**
         * Whether the node is loaded.
         * @type {boolean}
         */
		this.loaded = false;
		/**
         * Handlers for one-time disposal actions.
         * @type {Function[]}
         */
		this.oneTimeDisposeHandlers = [];
	}
	/**
	 * Determines if the node is a geometry node.
	 * @returns {boolean} True if the node is a geometry node.
	 */
	isGeometryNode(){
		return true;
	}
	/**
	 * Retrieves the level of the node in the octree.
	 * @returns {number} The level of the node.
	 */
	getLevel(){
		return this.level;
	}
	/**
	 * Determines if the node is a tree node.
	 * @returns {boolean} False, as this is a geometry node.
	 */
	isTreeNode(){
		return false;
	}
	/**
	 * Checks whether the node's geometry is loaded.
	 * @returns {boolean} True if the node's geometry is loaded.
	 */
	isLoaded(){
		return this.loaded;
	}
	/**
	 * Gets the bounding sphere of the node.
	 * @returns {THREE.Sphere} The bounding sphere of the node.
	 */
	getBoundingSphere(){
		return this.boundingSphere;
	}
	/**
	 * Gets the bounding box of the node.
	 * @returns {THREE.Box3} The bounding box of the node.
	 */
	getBoundingBox(){
		return this.boundingBox;
	}
	/**
	 * Gets the children of this node.
	 * @returns {PointCloudOctreeGeometryNode[]} An array of child nodes.
	 */
	getChildren(){
		let children = [];

		for (let i = 0; i < 8; i++) {
			if (this.children[i]) {
				children.push(this.children[i]);
			}
		}

		return children;
	}

	getBoundingBox(){
		return this.boundingBox;
	}
	getTightBoundingBox(){
		return this.tightBoundingBox;
	}

	setTightBoundingBox(tightBoundingBox){
		return this.tightBoundingBox = tightBoundingBox;
	}
	/**
	 * Constructs and gets the URL for this node.
	 * @returns {string} The URL for this node.
	 */
	getURL(){
		let url = '';

		let version = this.pcoGeometry.loader.version;

		if (version.equalOrHigher('1.5')) {
			url = this.pcoGeometry.octreeDir + '/' + this.getHierarchyPath() + '/' + this.name;
		} else if (version.equalOrHigher('1.4')) {
			url = this.pcoGeometry.octreeDir + '/' + this.name;
		} else if (version.upTo('1.3')) {
			url = this.pcoGeometry.octreeDir + '/' + this.name;
		}

		return url;
	}
	/**
	 * Constructs the hierarchy path for this node.
	 * @returns {string} The hierarchy path.
	 */
	getHierarchyPath(){
		let path = 'r/';

		let hierarchyStepSize = this.pcoGeometry.hierarchyStepSize;
		let indices = this.name.substr(1);

		let numParts = Math.floor(indices.length / hierarchyStepSize);
		for (let i = 0; i < numParts; i++) {
			path += indices.substr(i * hierarchyStepSize, hierarchyStepSize) + '/';
		}

		path = path.slice(0, -1);

		return path;
	}
	/**
	 * Adds a child node to this node.
	 * @param {PointCloudOctreeGeometryNode} child - The child node to add.
	 */
	addChild(child) {
		this.children[child.index] = child;
		child.parent = this;
	}
	/**
	 * Initiates the loading of this node.
	 */
	load(){
		if (this.loading === true || this.loaded === true || Potree.numNodesLoading >= Potree.maxNodesLoading) {
			return;
		}

		this.loading = true;

		Potree.numNodesLoading++;

		if (this.pcoGeometry.loader.version.equalOrHigher('1.5')) {
			if ((this.level % this.pcoGeometry.hierarchyStepSize) === 0 && this.hasChildren) {
				this.loadHierachyThenPoints();
			} else {
				this.loadPoints();
			}
		} else {
			this.loadPoints();
		}
	}
	/**
	 * Loads the points for this node.
	 */
	loadPoints(){
		this.pcoGeometry.loader.load(this);
	}
	/**
	 * Loads the hierarchy and then the points for this node.
	 */
	loadHierachyThenPoints(){
		let node = this;

		// COMPAT(xhr-binary): normalize binary payloads across browser XHR behaviors.
		let toArrayBuffer = (bufferLike) => {
			if (bufferLike instanceof ArrayBuffer) {
				return bufferLike;
			}

			if (ArrayBuffer.isView(bufferLike)) {
				return bufferLike.buffer.slice(
					bufferLike.byteOffset,
					bufferLike.byteOffset + bufferLike.byteLength
				);
			}

			if (typeof bufferLike === 'string') {
				let buffer = new ArrayBuffer(bufferLike.length);
				let view = new Uint8Array(buffer);
				for (let i = 0; i < bufferLike.length; i++) {
					view[i] = bufferLike.charCodeAt(i) & 0xff;
				}
				return buffer;
			}

			return null;
		};

		// load hierarchy
		let callback = function (node, hbuffer) {

			let tStart = performance.now();

			hbuffer = toArrayBuffer(hbuffer);
			if (hbuffer === null) {
				console.log('Failed to decode hierarchy buffer for node ' + node.name);
				Potree.numNodesLoading--;
				return;
			}

			let view = new DataView(hbuffer);

			let stack = [];
			let children = view.getUint8(0);
			let numPoints = view.getUint32(1, true);
			node.numPoints = numPoints;
			stack.push({children: children, numPoints: numPoints, name: node.name});

			let decoded = [];

			let offset = 5;
			while (stack.length > 0) {
				let snode = stack.shift();
				let mask = 1;
				for (let i = 0; i < 8; i++) {
					if ((snode.children & mask) !== 0) {
						let childName = snode.name + i;

						let childChildren = view.getUint8(offset);
						let childNumPoints = view.getUint32(offset + 1, true);

						stack.push({children: childChildren, numPoints: childNumPoints, name: childName});

						decoded.push({children: childChildren, numPoints: childNumPoints, name: childName});

						offset += 5;
					}

					mask = mask * 2;
				}

				if (offset === hbuffer.byteLength) {
					break;
				}
			}

			// console.log(decoded);

			let nodes = {};
			nodes[node.name] = node;
			let pco = node.pcoGeometry;

			for (let i = 0; i < decoded.length; i++) {
				let name = decoded[i].name;
				let decodedNumPoints = decoded[i].numPoints;
				let index = parseInt(name.charAt(name.length - 1));
				let parentName = name.substring(0, name.length - 1);
				let parentNode = nodes[parentName];
				let level = name.length - 1;
				let boundingBox = Utils.createChildAABB(parentNode.boundingBox, index);

				let currentNode = new PointCloudOctreeGeometryNode(name, pco, boundingBox);
				currentNode.level = level;
				currentNode.numPoints = decodedNumPoints;
				currentNode.hasChildren = decoded[i].children > 0;
				currentNode.spacing = pco.spacing / Math.pow(2, level);
				parentNode.addChild(currentNode);
				nodes[name] = currentNode;
			}

			let duration = performance.now() - tStart;
			if(duration > 5){
				let msg = `duration: ${duration}ms, numNodes: ${decoded.length}`;
				console.log(msg);
			}

			node.loadPoints();
		};
		if ((node.level % node.pcoGeometry.hierarchyStepSize) === 0) {
			// let hurl = node.pcoGeometry.octreeDir + "/../hierarchy/" + node.name + ".hrc";
			let hurl = node.pcoGeometry.octreeDir + '/' + node.getHierarchyPath() + '/' + node.name + '.hrc';

			let xhr = XHRFactory.createXMLHttpRequest();
			xhr.open('GET', hurl, true);
			xhr.responseType = 'arraybuffer';
			xhr.overrideMimeType('text/plain; charset=x-user-defined');
			xhr.onreadystatechange = () => {
				if (xhr.readyState === 4) {
					if (xhr.status === 200 || xhr.status === 0) {
						let hbuffer = xhr.response;
						if (hbuffer === null && (xhr.responseType === '' || xhr.responseType === 'text')) {
							try {
								hbuffer = xhr.responseText;
							} catch (e) {
								hbuffer = null;
							}
						}
						callback(node, hbuffer);
					} else {
						console.log('Failed to load file! HTTP status: ' + xhr.status + ', file: ' + hurl);
						Potree.numNodesLoading--;
					}
				}
			};
			try {
				xhr.send(null);
			} catch (e) {
				console.log('fehler beim laden der punktwolke: ' + e);
			}
		}
	}
	/**
	 * Gets the number of points in the node.
	 * @returns {number} The number of points in the node.
	 */
	getNumPoints(){
		return this.numPoints;
	}
	/**
	 * Disposes of the node's resources.
	 */
	dispose(){
		if (this.geometry && this.parent != null) {
			this.geometry.dispose();
			this.geometry = null;
			this.loaded = false;

			this.dispatchEvent( { type: 'dispose' } );
			
			for (let i = 0; i < this.oneTimeDisposeHandlers.length; i++) {
				let handler = this.oneTimeDisposeHandlers[i];
				handler();
			}
			this.oneTimeDisposeHandlers = [];
		}
	}
	
}

PointCloudOctreeGeometryNode.IDCount = 0;
